import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { skipReasonPhrase, SKIP_REASON_LABELS } from '@/lib/dispatchOutcome'
import { useSocket } from '@/providers/SocketContext'
import type { BulkSendResultEvent } from '@/types/SocketEvents'

/** Janela de silêncio: o back emite 1 evento POR contato não-enviado em rajada;
 *  só toasta quando a rajada assenta, senão N contatos = N toasts. */
const FLUSH_DELAY_MS = 2000
const MAX_NAMES = 5

export function summarizeBulkSendFailures(events: BulkSendResultEvent[]): { title: string; description: string } {
  const total = events.length
  /* Os motivos saem dos EVENTOS, ordenados pela tabela — não das chaves da
     tabela. Iterar a tabela descartava motivo desconhecido, que continuava
     contado no total: "3 contatos" com dois listados. */
  const ordem = Object.keys(SKIP_REASON_LABELS)
  const posicao = (r: string) => {
    const i = ordem.indexOf(r)
    return i === -1 ? ordem.length : i
  }
  const parts = Array.from(new Set(events.map((e) => e.reason)))
    .sort((a, b) => posicao(a) - posicao(b))
    .map((reason) => ({ reason, count: events.filter((e) => e.reason === reason).length }))
    .map(({ reason, count }) => (total === 1 ? skipReasonPhrase(reason) : `${count} ${skipReasonPhrase(reason)}`))
  const title = total === 1
    ? `1 contato não recebeu o template: ${parts.join(', ')}`
    : `${total} contatos não receberam o template: ${parts.join(', ')}`
  const names = events
    .slice(0, MAX_NAMES)
    .map((e) => e.contact.name || e.contact.phone || e.contact.wa_user_id || 'sem identificador')
  const rest = total - names.length
  const description = rest > 0 ? `${names.join(', ')} e mais ${rest}` : names.join(', ')
  return { title, description }
}

/** Agrega os `bulk_send_result` de um disparo num toast único de resumo. */
export function useBulkSendResultToast() {
  const { socket } = useSocket()
  const bufferRef = useRef<BulkSendResultEvent[]>([])
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Guarda SÓ em `socket` (não `socketReady`) — mesma razão do useReadReceiptsLive:
  // listener precisa sobreviver à reconexão; registrar em socket desconectado é inofensivo.
  useEffect(() => {
    if (!socket) return
    const flush = () => {
      timerRef.current = null
      const events = bufferRef.current
      bufferRef.current = []
      if (events.length === 0) return
      const { title, description } = summarizeBulkSendFailures(events)
      toast.warning(title, { description, duration: 10000 })
    }
    const onResult = (evt: BulkSendResultEvent) => {
      bufferRef.current.push(evt)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(flush, FLUSH_DELAY_MS)
    }
    socket.on('bulk_send_result', onResult)
    return () => {
      socket.off('bulk_send_result', onResult)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = null
      bufferRef.current = []
    }
  }, [socket])
}
